import type { Tool } from "@ag-ui/core";
// Generated wire-contract data shared with mobile, independent of application modules.
// eslint-disable-next-line @nx/enforce-module-boundaries
import definitions from "../../../contracts/frontend-tools-v1.json";
import { parseComponent, type ChatComponent } from "./assistant-components";

export const frontendDisplayTools: Tool[] = definitions;
export type DisplayToolName = "show_stage_breakdown" | "show_hypnogram";
export interface RenderResult {
  id: string; role: "tool"; toolCallId: string; content: string;
  error?: "invalid_payload" | "invalid_timeline" | "renderer_unavailable" | "render_failed";
}
export interface DisplayCall {
  id: string; name: DisplayToolName; parentMessageId: string; arguments: string; component: ChatComponent;
}
export interface SavedToolCall { id: string; type: "function"; function: { name: string; arguments: string } }

export function stableDisplayJSON(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableDisplayJSON).join(",")}]`;
  if (value && typeof value === "object") return `{${Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, val]) => `${JSON.stringify(key)}:${stableDisplayJSON(val)}`).join(",")}}`;
  return JSON.stringify(value);
}
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new TypeError("Invalid display call");
  return value as Record<string, unknown>;
}
export function parseRenderResult(value: unknown): RenderResult {
  const v = object(value);
  if (typeof v.id !== "string" || !/^[a-z0-9]{15}$/.test(v.id) || v.role !== "tool" ||
    typeof v.toolCallId !== "string" || !/^call_[a-z0-9]{15}$/.test(v.toolCallId) ||
    typeof v.content !== "string" || !v.content.trim() || Array.from(v.content).length > 500 || new TextEncoder().encode(v.content).length > 2000 ||
    Object.keys(v).some(key => !["id", "role", "toolCallId", "content", "error"].includes(key)) ||
    (v.error !== undefined && !["invalid_payload", "invalid_timeline", "renderer_unavailable", "render_failed"].includes(String(v.error)))) throw new TypeError("Invalid rendering result");
  return v as unknown as RenderResult;
}
export function parseDisplayCall(value: unknown, parent: string, conversation: string): DisplayCall {
  const call = object(value), fn = object(call.function);
  if (typeof call.id !== "string" || !/^call_[a-z0-9]{15}$/.test(call.id) || call.type !== "function" ||
    !["show_stage_breakdown", "show_hypnogram"].includes(String(fn.name)) || typeof fn.arguments !== "string" ||
    new TextEncoder().encode(fn.arguments).length > 65536 || Object.keys(call).some(key => !["id", "type", "function"].includes(key)) ||
    Object.keys(fn).some(key => !["name", "arguments"].includes(key))) throw new TypeError("Unsupported or invalid display call");
  const args = object(JSON.parse(fn.arguments));
  if (args.schema !== "neoma.display-component/1" || Object.keys(args).some(key => !["schema", "component"].includes(key))) throw new TypeError("Invalid display arguments");
  const component = parseComponent(args.component);
  if (component.conversation_id !== conversation || component.message_id !== parent || !component.payload ||
    component.payload.type !== (fn.name === "show_hypnogram" ? "hypnogram" : "sleep_stage_breakdown")) throw new TypeError("Display association mismatch");
  return { id: call.id, name: fn.name as DisplayToolName, parentMessageId: parent, arguments: fn.arguments, component };
}

/** Saved state wins by revision; immutable arguments always retain initial state. */
export function reconcileDisplayComponents(components: ChatComponent[], calls: DisplayCall[]): ChatComponent[] {
  const merged = new Map(components.map(component => [component.id, component]));
  for (const call of calls) {
    const initial = call.component, saved = merged.get(initial.id);
    if (saved && (saved.conversation_id !== initial.conversation_id || saved.message_id !== initial.message_id || saved.request_id !== initial.request_id ||
      stableDisplayJSON(saved.payload) !== stableDisplayJSON(initial.payload) ||
      (saved.state.revision === initial.state.revision && stableDisplayJSON(saved.state) !== stableDisplayJSON(initial.state)))) throw new TypeError("Display snapshot conflict");
    if (!saved || saved.state.revision < initial.state.revision) merged.set(initial.id, initial);
  }
  return [...merged.values()];
}
